import styled, { css } from 'styled-components';
import { spacing } from '_utils/props';
import { mq, color, heading_m, text_s, svg, hideOnPrint } from '_utils/styles';
import { cssInlineSvg } from '_utils/styles/svg';
import { Grid, Link } from '_utils/components/Corporate';
import { handleSectionTheme } from '_utils/styles/section';
import { CardThemes, ColourSchema } from './definitions';

const cardThemes: ColourSchema = {
  [CardThemes.Green]: {
    main: 'all.green',
    tint: 'all.greenTint45'
  },
  [CardThemes.Fuchsia]: {
    main: 'all.newtownFuchsia',
    tint: 'all.newtownFuchsiaTint45'
  },
  [CardThemes.Purple]: {
    main: 'all.darlinghurstPurple',
    tint: 'all.darlinghurstPurpleTint45'
  },
  [CardThemes.Orange]: {
    main: 'all.annandaleOrange',
    tint: 'all.annandaleOrangeTint45'
  },
  [CardThemes.Blue]: {
    main: 'all.pyrmontBlueTint75',
    tint: 'all.pyrmontBlueTint45'
  },
  [CardThemes.Brown]: {
    main: 'all.theRocksBrown',
    tint: 'all.theRocksBrownTint45'
  }
};
export const FootLinkRow = styled(Grid.Row)`
  ${hideOnPrint}
`;
export const Section = styled.section<{ $sectionTheme: string }>`
  ${({ $sectionTheme }) => handleSectionTheme($sectionTheme)}
  // cancel out main container rule of adding spacing
  &&& {
    padding-bottom: 0;
  }

  @media print {
    page-break-inside: avoid;
  }
`;

export const IconWrapper = styled.div`
  ${({ theme }) => spacing({ theme, $mb: 2 })}
  ${svg(27, 27)}
  width: 27px;
`;

const handleCardTheme = ({ $cardTheme }) => {
  const { tint, main } = cardThemes[$cardTheme];

  return css`
    border-color: ${color(tint)};

    ${IconWrapper} {
      path {
        fill: ${color(main)};
      }
    }
  `;
};

export const ContentListItem = styled.div<{ $cardTheme: CardThemes }>`
  width: 224px;
  border: 3px solid transparent;
  border-radius: ${({ theme }) => theme.card.borderRadius}px;
  ${({ theme }) => spacing({ theme, $p: 3 })}
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  background-color: white;

  & + & {
    ${({ theme }) => spacing({ theme, $ml: 4 })}
  }

  ${mq('md')} {
    width: 288px;
  }

  ${mq('lg')} {
    width: 100%;
  }

  ${handleCardTheme};

  .flickity-resize & {
    top: 0;
    bottom: 0;
  }
`;

export const ContentListItemTitle = styled.h3`
  ${heading_m}
  margin: 0;
`;

export const ContentListItemDesc = styled.p`
  ${text_s}
  margin: 0;
  ${({ theme }) => spacing({ theme, $mt: 1 })}
  color: ${({ theme }) => theme.colors.all.deepBlueTint75};
`;

export const ContentListItemLinkWrapper = styled.div`
  ${({ theme }) => spacing({ theme, $mt: 3 })}
`;

export const LinkWrapper = styled.div`
  flex: 1 0 auto;
`;

export const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  ${({ theme }) => spacing({ theme, $mt: 3 })}
  @media print {
    page-break-inside: avoid;
  }
`;

export const ListItem = styled.li`
  position: relative;
  padding-left: 24px;

  & + & {
    ${({ theme }) => spacing({ theme, $mt: 2 })}
  }

  &:before {
    content: '';
    position: absolute;
    top: 5px;
    left: 0;
    display: block;
    width: 12px;
    height: 12px;
    background-image: ${cssInlineSvg(
      `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.7667 5.68333C11.727 5.58104 11.6675 5.48759 11.5917 5.40833L6.59166 0.408334C6.51396 0.330635 6.42172 0.269001 6.3202 0.226951C6.21868 0.184901 6.10987 0.163258 5.99999 0.163258C5.77807 0.163258 5.56524 0.251414 5.40832 0.408334C5.33062 0.486033 5.26899 0.578275 5.22694 0.679793C5.18489 0.781311 5.16325 0.890118 5.16325 1C5.16325 1.22192 5.2514 1.43475 5.40832 1.59167L8.99166 5.16667H0.99999C0.778976 5.16667 0.567015 5.25447 0.410734 5.41075C0.254454 5.56703 0.166656 5.77899 0.166656 6C0.166656 6.22101 0.254454 6.43298 0.410734 6.58926C0.567015 6.74554 0.778976 6.83333 0.99999 6.83333H8.99166L5.40832 10.4083C5.33022 10.4858 5.26822 10.578 5.22591 10.6795C5.18361 10.7811 5.16182 10.89 5.16182 11C5.16182 11.11 5.18361 11.2189 5.22591 11.3205C5.26822 11.422 5.33022 11.5142 5.40832 11.5917C5.48579 11.6698 5.57796 11.7318 5.67951 11.7741C5.78106 11.8164 5.88998 11.8382 5.99999 11.8382C6.11 11.8382 6.21892 11.8164 6.32047 11.7741C6.42202 11.7318 6.51419 11.6698 6.59166 11.5917L11.5917 6.59167C11.6675 6.51241 11.727 6.41896 11.7667 6.31667C11.85 6.11378 11.85 5.88622 11.7667 5.68333Z" fill="#2DB84B"/></svg>`
    )};
  }
`;

export const LinkItem = styled(Link)`
  a {
    display: block;
  }
`;

export const EditorDiv = styled.div``;

export const GridItem = styled(Grid.Item)`
  @media print {
    max-width: 33.3333%;
  }
`;
